import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RecordsTable, type Column, type FilterDef, type GroupDef } from '../components/RecordsTable/RecordsTable';
import { mcpCall } from '../lib/mcp';

interface InventoryRow {
  warehouse_id: number;
  sku_id: number;
  baseline_date: string;
  baseline_qty: number;
  units_out_since: number;
  on_hand: number;
}

// Minimal SKU shape we need to resolve sku_id → the human SKU code shown on Products, plus the
// size_merv_id used to group inventory into MERV sections. Comes from the SAME listSkus query the
// Products catalog uses (identical key + args below), so the code rendered here is the exact value
// the catalog shows.
interface SkuLite {
  id: number;
  sku_code: string;
  shopify_id: string | null;
  size_merv_id: number;
}

interface SizeMervRow {
  id: number;
  merv: number;
}

// The MERV tiers the schema allows, in display order — matches the Products catalog. Each has a
// --color-merv-N token used for the section accent.
const MERV_TIERS = [5, 8, 11, 13] as const;
const UNASSIGNED = 'unassigned';

const FILTERS: FilterDef<InventoryRow>[] = [
  {
    key: 'stock',
    label: 'Stock',
    options: [
      { value: 'all', label: 'All' },
      { value: 'in_stock', label: 'In stock' },
      { value: 'out_of_stock', label: 'Out of stock' },
    ],
    match: (r, v) => (v === 'out_of_stock' ? r.on_hand <= 0 : v === 'in_stock' ? r.on_hand > 0 : true),
  },
];

export function Inventory() {

  // Same key + args as ProductsCatalog's listSkus query → shared react-query cache, so the SKU
  // codes here are identically the ones the Products page renders (no second source of truth).
  const skusQ = useQuery({
    queryKey: ['mcp', 'listSkus', { limit: 200 }],
    queryFn: async () => (await mcpCall<{ skus: SkuLite[] }>('listSkus', { limit: 200 })).skus ?? [],
  });

  // Same key + args as the Products catalog's listSizeMervs query → shared cache. Maps a SKU's
  // size_merv_id to its MERV tier, so inventory groups by MERV exactly as the catalog does.
  const mervQ = useQuery({
    queryKey: ['mcp', 'listSizeMervs', { limit: 500 }],
    queryFn: async () =>
      (await mcpCall<{ sizeMervs: SizeMervRow[] }>('listSizeMervs', { limit: 500 })).sizeMervs ?? [],
  });

  const skuById = useMemo(() => {
    const m = new Map<number, SkuLite>();
    for (const s of skusQ.data ?? []) m.set(s.id, s);
    return m;
  }, [skusQ.data]);

  const mervById = useMemo(() => {
    const m = new Map<number, number>();
    for (const sm of mervQ.data ?? []) m.set(sm.id, sm.merv);
    return m;
  }, [mervQ.data]);

  // Resolve the real SKU code for a row; fall back to the id (as "#123") only if the catalog
  // hasn't resolved it yet — never a bare number masquerading as a code.
  const skuCode = (r: InventoryRow) => skuById.get(r.sku_id)?.sku_code ?? `#${r.sku_id}`;

  // sku_id → size_merv_id → merv (null until resolved, or if the SKU is unmapped).
  const mervOf = (r: InventoryRow): number | null => {
    const s = skuById.get(r.sku_id);
    if (!s) return null;
    return mervById.get(s.size_merv_id) ?? null;
  };

  // Group inventory into MERV sections, mirroring the catalog: known tiers first (in order),
  // any other present values numerically, then the Unassigned bucket last.
  const group: GroupDef<InventoryRow> = {
    keyOf: (r) => mervOf(r) ?? UNASSIGNED,
    label: (k) => (k === UNASSIGNED ? 'Unassigned' : `MERV ${k}`),
    accent: (k) => (k === UNASSIGNED ? 'var(--border-default)' : `var(--color-merv-${k})`),
    order: (a, b) => {
      if (a === UNASSIGNED) return 1;
      if (b === UNASSIGNED) return -1;
      const ia = (MERV_TIERS as readonly number[]).indexOf(Number(a));
      const ib = (MERV_TIERS as readonly number[]).indexOf(Number(b));
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return Number(a) - Number(b);
    },
  };

  const columns: Column<InventoryRow>[] = [
    { key: 'warehouse_id', header: 'Warehouse', numeric: true, sortable: true },
    {
      key: 'sku',
      header: 'SKU',
      sortable: true,
      sortAccessor: (r) => skuById.get(r.sku_id)?.sku_code ?? `#${r.sku_id}`,
      render: (r) => skuCode(r),
    },
    // B4: sort by baseline date (the "date-range sort" ask — column sort asc/desc; a true
    // date-range *filter* would need a picker, noted as a follow-up in the PR).
    { key: 'baseline_date', header: 'Baseline date', sortable: true },
    { key: 'baseline_qty', header: 'Baseline', numeric: true, sortable: true },
    { key: 'units_out_since', header: 'Units out', numeric: true, sortable: true },
    {
      key: 'on_hand',
      header: 'On hand',
      numeric: true,
      sortable: true,
      render: (r) =>
        r.on_hand <= 0 ? <span className="records__tag records__tag--warn">{r.on_hand}</span> : r.on_hand,
    },
  ];

  // The "Reseed from Shopify" control that used to live here was removed on 2026-09-03: Shopify
  // was cancelled and the MCP no longer offers the action. Baselines are recorded by the
  // warehouse, not pulled from a channel.

  return (
    <RecordsTable<InventoryRow>
      title="Inventory"
      intro="View current stock levels for all products. On-hand quantities update automatically as sales are recorded against each warehouse baseline."
      tool="listCurrentInventory"
      args={{ limit: 200 }}
      select={(r) => (r as { inventory: InventoryRow[] }).inventory ?? []}
      columns={columns}
      rowKey={(r) => `${r.warehouse_id}:${r.sku_id}`}
      emptyMessage="No inventory rows yet. Baselines appear once warehouse stock is recorded."
      refreshable
      searchable={{
        placeholder: 'Search by SKU code or warehouse id…',
        match: (r, q) => [skuCode(r), String(r.warehouse_id)].some((v) => v.toLowerCase().includes(q)),
      }}
      filters={FILTERS}
      group={group}
    />
  );
}
